import { ConsoleLogger } from '@proposify/frontend-observability';
import type { BlockContent } from '../grid/reduxStore/editorSlice';
import type { BlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import type { SignatureBox } from 'services/entities/SignatureTypes';
import type { VariableNode } from 'services/entities/VariablesEntity';

/**
 * Clipboard utilities for copying and pasting data
 */

export interface SerializeForClipboardProps {
  data: BlockContent | SignatureBox | VariableNode;
  styles?: BlockSettings;
  sourceContentId?: string;
  sectionId?: string;
  isTemplateEditor?: boolean;
}

export const serializeForClipboard = (data: SerializeForClipboardProps): string => {
  try {
    return JSON.stringify(data);
  } catch (error) {
    return '';
  }
};

const isVariableClipboardPayload = (blockData: SerializeForClipboardProps) => {
  const d = (blockData as any)?.data;
  return !!(d && typeof d === 'object' && typeof d.name === 'string' && typeof d.friendlyName === 'string');
};

export const deserializeFromClipboard = (clipboardText: string | null): any => {
  try {
    if (!clipboardText) return null;
    return JSON.parse(clipboardText);
  } catch (error) {
    return null;
  }
};

/**
 * Copies block data to clipboard
 * @param blockData The block data to copy
 * @returns Promise that resolves to true if copy was successful, false otherwise
 */
export const copyBlockToClipboard = async (blockData: SerializeForClipboardProps): Promise<boolean> => {
  try {
    if (isVariableClipboardPayload(blockData)) {
      const marker = `[[pvar:${JSON.stringify(blockData)}]]`;
      await navigator.clipboard.writeText(marker);
      return true;
    }

    const serializedData = serializeForClipboard(blockData);
    if (!serializedData) return false;

    await navigator.clipboard.writeText(serializedData);
    return true;
  } catch (error) {
    ConsoleLogger.error(error);
    return false;
  }
};

/**
 * Reads clipboard content and attempts to parse it as block data
 * @returns Promise that resolves to parsed block data, or null if invalid
 */
export const readBlockFromClipboard = async (): Promise<SerializeForClipboardProps | null> => {
  try {
    const clipboardText = await navigator.clipboard.readText();
    return deserializeFromClipboard(clipboardText);
  } catch (error) {
    return null;
  }
};
